import { CurrencyService } from './currency-service';
import { EventAggregator, inject, observable, watch } from 'aurelia';

import { User } from './models/user';
import { ApiService } from './api-service';
import { CustomerService } from './customer-service';
import { websiteShortCode } from '../environment';
import { TokenService } from './token-service';
import notifications from '../resources/notifications-manager';
import { LanguageWebsiteService } from './language-website-service';
import { SignalBindingBehavior } from '@aurelia/runtime-html';
import countryToCurrency from 'country-to-currency';
import { Geolocation } from './models/language/geolocation';
import { Currency } from './models';
import { countryToLanguage } from './models/language/country-to-language';
import { Language } from '../state/models/ILanguage';

const ADMIN_VIEW = 'admin_view';
const CART_KEY = 'cart';
const CURRENT_BLOGS_PAGE_VIEW = 'current_blogs_page_view';
const WEBSITE_SHORTCODE = 'website_shortcode';
const CURRENCY_KEY = 'currency';
const PREVIOUS_CURRENCY_KEY = 'previous_currency';
const LANGUAGE_KEY = 'language';

@inject(
    ApiService,
    EventAggregator,
    CustomerService,
    TokenService,
    LanguageWebsiteService,
    CurrencyService,
    SignalBindingBehavior
)
export class SessionService {
    isAuthenticated = false;
    currentUser: User | null = null;
    @observable geolocation: Geolocation;
    userProxy;
    CURRENCY_OPTIONS: any[] = [];
    userCart;
    userBlacklist;
    private geolocationPromise: Promise<Geolocation> | null = null;

    constructor(
        private apiService: ApiService,
        private eventAggregator: EventAggregator,
        private customerService: CustomerService,
        private tokenService: TokenService,
        private languageWebsiteService: LanguageWebsiteService,
        private CurrencyService: CurrencyService
    ) {
    }

    async getProfile() {
        if (await this.tokenService.isTokenValid()) {
            if (this.currentUser) {
                return this.currentUser;
            }

            this.currentUser = await this.apiService.doGet('profile');

            if (!this.currentUser?.isDeleted) {
                this.tokenService.saveToken(this.currentUser?.refreshToken?.token);
            } else if (this.currentUser?.isDeleted) {
                notifications.toast('As a result you have been logged out, please Sign up again to access your account or contact Support.', 'info', 'Account deleted').show();
                await this.logout(true);
            }

            if (this.currentUser?.userBlacklist?.length > 0) {
                this.userBlacklist = this.currentUser?.userBlacklist;
            }

            return this.currentUser;
        }

        return null;
    }

    async logout(skipLogoutCall = false) {
        try {
            if (!skipLogoutCall) await this.apiService.doDelete('Logout');
            this.eventAggregator.publish('untrack-user', { userId: this.currentUser?.id });
            this.saveBlogsCurrentPageView(1);
        } finally {
            this.tokenService.destroyToken();
            this.currentUser = null;
            this.userCart = null;
            this.isAuthenticated = false;
            this.eventAggregator.publish('user-updated', { forDelete: true });
            await this.getCart();
            await this.destroyCart();
        }
    }

    saveAdminView(bool: boolean) {
        window.localStorage[ADMIN_VIEW] = bool;
        this.eventAggregator.publish('admin-view-updated', { bool: bool });
    }

    getAdminView() {
        return (window.localStorage[ADMIN_VIEW] === 'true');
    }

    checkRolesForPanelAccess(user: User) {
        return !!user?.roles?.find(x => x.toLowerCase() === 'viewadminpanel');
    }

    saveBlogsCurrentPageView(page: number) {
        window.localStorage[CURRENT_BLOGS_PAGE_VIEW] = page;
    }

    async getCart() {
        if (this.currentUser) {
            if (this.userCart) {
                this.userCart.forEach((e, index) => {
                    this.userCart[index].tempQuantity = e.quantity;
                    this.userCart[index].quantity = new Intl.NumberFormat('en-US').format(Number(e.quantity));
                    this.userCart[index].selectedQuantity = e.quantity;
                });
                return this.userCart;
            }
            this.userCart = await this.customerService.getUserCartProducts();
            this.userCart.forEach((e, index) => {
                this.userCart[index].cartItemId = index + 1;
                this.userCart[index].tempQuantity = e.quantity;
                this.userCart[index].quantity = new Intl.NumberFormat('en-US').format(Number(e.quantity));
                this.userCart[index].selectedQuantity = e.quantity;
            });
            return this.userCart;
        }
        const stringCart = window.localStorage[CART_KEY];
        if (stringCart && stringCart !== '[]' && stringCart !== '') {
            const cartItems = JSON.parse(stringCart);
            cartItems.forEach((e, index) => {
                cartItems[index].tempQuantity = e.quantity;
            });
            return cartItems;
        }
        this.destroyCart();


    }

    async getCartCount() {
        if (this.currentUser) {
            return await this.customerService.getUserCartProductsCount();
        }
        const stringCart = window.localStorage[CART_KEY];
        if (stringCart && stringCart !== '[]' && stringCart !== '') {
            const cartItems = JSON.parse(stringCart);
            return cartItems.length;
        }
        return 0;
    }

    async destroyCart() {
        if (this.currentUser) {
            await this.customerService.destroyUserCartProducts();
        }
        window.localStorage.removeItem(CART_KEY);
        this.userCart = null;
        this.eventAggregator.publish('cart-updated', { cart: [] });
    }

    getWebsiteShortcode(): string {
        return window.localStorage[WEBSITE_SHORTCODE];
    }

    setWebsiteShortcode(websiteShortcode: string) {
        window.localStorage[WEBSITE_SHORTCODE] = websiteShortcode;
    }

    async getCurrencyOptions() {
        const currencies = await this.CurrencyService.getActiveCurrenciesByWebsite(websiteShortCode());
        this.CURRENCY_OPTIONS = [];
        if (currencies?.length) {
            for (const currency of currencies) {
                this.CURRENCY_OPTIONS.push({
                    id: currency.id,
                    value: currency.code,
                    text: currency.code,
                    displayName: currency.description,
                    icon: currency.imagePath,
                    currencySymbol: currency.symbol,
                    isTrending: currency.isTrending
                });
            }
        }
        return this.CURRENCY_OPTIONS.sort((a, b) => a.value?.toLowerCase()?.localeCompare(b.value?.toLowerCase()));
    }

    checkIfUserAgentPrerender = () => navigator.userAgent.toLowerCase().indexOf('prerender') !== -1;


    async saveCurrency(currency: Currency) {
        if (!currency) return;
        await this.CurrencyService.getStoredCurrencyRates(currency);
        window.localStorage[PREVIOUS_CURRENCY_KEY] = window.localStorage[CURRENCY_KEY];
        const oldCurrency = window.localStorage[PREVIOUS_CURRENCY_KEY];
        window.localStorage[CURRENCY_KEY] = currency.code;
        this.eventAggregator.publish('currency-updated', { currency: currency, oldCurrency: oldCurrency });
    }

    saveLanguage(language: Language) {
        window.localStorage[LANGUAGE_KEY] = JSON.stringify(language);
        this.eventAggregator.publish('language-updated', { language: language });
    }

    // Add a watch handler for when geolocation changes
    @watch('geolocation')
    geolocationChanged(newValue: Geolocation, oldValue: Geolocation) {
        if (newValue && newValue !== oldValue) {
            this.eventAggregator.publish('geolocation-updated', { geolocation: newValue });
            // Resolve any pending promise
            if (this.geolocationPromise) {
                this.geolocationPromise = null;
            }
        }
    }

    async getCountry() {
        if (!this.geolocation && !this.geolocationPromise) {
            this.geolocationPromise = new Promise<Geolocation>((resolve) => {
                const subscription = this.eventAggregator.subscribe('geolocation-updated', payload => {
                    subscription.dispose();
                    resolve(payload.geolocation);
                });

                setTimeout(() => {
                    if (this.geolocationPromise) {
                        subscription.dispose();
                        resolve({ countryCode: 'US' } as Geolocation); // Default fallback
                        this.geolocationPromise = null;
                    }
                }, 100);
            });
        }

        if (!this.geolocation) {
            const result = await this.geolocationPromise;
            if (result && !this.geolocation) {
                this.geolocation = result;
            }
        }

        return this.geolocation;
    }

    async getCurrencyByCountry() {
        const country = await this.getCountry();
        if (country && country.countryCode) {
            const options = await this.getCurrencyOptions();
            return options.find(x => x.value === countryToCurrency[country.countryCode])?.value ?? 'USD';
        }
        return 'USD';
    }

    async getLanguage() {
        if (!this.languageWebsiteService.languages) await this.languageWebsiteService.getByWebsite();
        if (this.checkIfUserAgentPrerender()) return this.languageWebsiteService.languages?.find(x => x.language?.hrefLang?.includes('en'));

        const stringLanguage = window.localStorage[LANGUAGE_KEY];
        if (stringLanguage && stringLanguage !== '[]' && stringLanguage !== '' && stringLanguage !== 'undefined' && stringLanguage !== 'null') {
            const parsed = JSON.parse(stringLanguage);
            if (parsed?.language?.hrefLang) {
                return parsed;
            }
            window.localStorage.removeItem(LANGUAGE_KEY);
        }

        let hrefLang = await this.getLanguageByCountry();
        hrefLang = hrefLang ?? 'en';
        const language = this.languageWebsiteService.languages?.find(x => x.language?.hrefLang?.includes(hrefLang));

        if (language) {
            this.saveLanguage(language);
        }

        return language;
    }

    async getLanguageByCountry() {
        const country = await this.getCountry();
        if (!country) return null;

        return countryToLanguage.get(country.countryCode) ?? 'en';
    }
}
